import assert from 'shared/assert'
import json_api_schema from './json_api_schema.generated'

import type { RelationshipsToTypes } from './json_api_schema.generated'

type Resource = ResourceTypes[keyof ResourceTypes]

type RequestResponse = {
	data: Resource[] | Resource
	included: Resource[]
}

type Relationship<TYPE> = {
	type: TYPE
	id: string
}

type RelationshipData = {
	data: Relationship<keyof ResourceTypes> | Relationship<keyof ResourceTypes>[]
}

export const get_resources = (response: RequestResponse) => Array.isArray(response.data)
	? [ ...response.included, ...response.data ]
	: [ ...response.included, response.data ]

export const add_resources_to_map = (
	resources: Resource[],
	type_name_to_resource_id_map: Map<keyof ResourceTypes, Map<string, Resource>>
) => {
	resources.forEach((resource: Resource) => {
		const map = type_name_to_resource_id_map.get(resource.type)

		if (map) {
			map.set(resource.id, resource)
		} else {
			type_name_to_resource_id_map.set(resource.type, new Map([
				[ resource.id, resource ],
			]))
		}
	})

	return type_name_to_resource_id_map
}

export const get_map = (response: RequestResponse) => {
	const type_name_to_resource_id_map = new Map<keyof ResourceTypes, Map<string, Resource>>()
	return add_resources_to_map(get_resources(response), type_name_to_resource_id_map)
}

export const get_resource = <TYPE extends keyof ResourceTypes>(type_name_to_resource_id_map: Map<keyof ResourceTypes, Map<string, Resource>>, type: TYPE, id: string) => {
	const resource_id_map = type_name_to_resource_id_map.get(type)

	assert(resource_id_map, `There had better be a resource id map for type "${ type }"`)

	const resource = resource_id_map.get(id) as undefined | ResourceTypes[TYPE]

	assert(resource, `There should be a resource of type "${ type }" with the id "${ id }"`)

	return resource
}

type MakeNullableIfOptionalAndSingular<IF_OPTIONAL_CHECK_IF_SINGULAR, IF_SINGULAR_MAKE_NULLABLE> = undefined extends IF_OPTIONAL_CHECK_IF_SINGULAR
	? IF_SINGULAR_MAKE_NULLABLE extends unknown[]
		? IF_SINGULAR_MAKE_NULLABLE
		: IF_SINGULAR_MAKE_NULLABLE | null
	: IF_SINGULAR_MAKE_NULLABLE

export const get_related = <
	RESOURCE extends Pick<Resource, `relationships`>,
	RELATIONSHIP_NAME extends keyof RelationshipsToTypes & keyof RESOURCE[`relationships`],
	RETURN extends MakeNullableIfOptionalAndSingular<RESOURCE[`relationships`][RELATIONSHIP_NAME], RelationshipsToTypes[RELATIONSHIP_NAME]>
>(
		type_name_to_resource_id_map: Map<keyof ResourceTypes, Map<string, Resource>>,
		resource: RESOURCE,
		relationship_name: RELATIONSHIP_NAME & keyof RESOURCE[`relationships`]
	): RETURN => {
	const relationship = resource.relationships[relationship_name] as RelationshipData

	assert(relationship, `Relationship "${ relationship_name.toString() }" must exist`)

	const relationship_data = relationship.data

	if (!relationship_data) {
		return null as RETURN
	}

	if (Array.isArray(relationship_data)) {
		return relationship_data.map(relationship => get_resource(type_name_to_resource_id_map, relationship.type, relationship.id)) as RETURN
	}

	return get_resource(type_name_to_resource_id_map, relationship_data.type, relationship_data.id) as RETURN
}

type Unpacked<T> = T extends (infer U)[] ? U : T

export const add_relationship = <
	RESOURCE extends Resource,
	RELATIONSHIP_NAME extends keyof RelationshipsToTypes,
	RELATED_RESOURCE extends Unpacked<RelationshipsToTypes[RELATIONSHIP_NAME]>
>(
		resource: RESOURCE,
		relationship_name: RELATIONSHIP_NAME & keyof RESOURCE[`relationships`],
		related_resource: RELATED_RESOURCE
	) => {
	const existing_relationship_data = resource.relationships[relationship_name]?.data || null

	const new_relationship_data = {
		type: related_resource.type,
		id: related_resource.id,
	}

	const relationship_is_many = json_api_schema[resource.type].relationships[relationship_name].many

	// @ts-expect-error typescript is confused about the relationships property
	resource.relationships[relationship_name] = {
		data: relationship_is_many
			? [ ...(Array.isArray(existing_relationship_data) ? existing_relationship_data : []), new_relationship_data ]
			: new_relationship_data,
	}

	return resource
}

export const add_empty_relationship = <
	RESOURCE extends Resource,
	RELATIONSHIP_NAME extends keyof RelationshipsToTypes
>(
		resource: RESOURCE,
		relationship_name: RELATIONSHIP_NAME & keyof RESOURCE[`relationships`]
	) => {
	const existing_relationship_data = resource.relationships[relationship_name]?.data || null

	const relationship_is_many = json_api_schema[resource.type].relationships[relationship_name].many

	// @ts-expect-error typescript is confused about the relationships property
	resource.relationships[relationship_name] = {
		data: relationship_is_many
			? Array.isArray(existing_relationship_data) ? existing_relationship_data : []
			: null,
	}

	return resource
}

export const remove_relationship = <
	RESOURCE extends Resource,
	RELATIONSHIP_NAME extends keyof RelationshipsToTypes,
	RELATED_RESOURCE extends Unpacked<RelationshipsToTypes[RELATIONSHIP_NAME]>
>(
		resource: RESOURCE,
		relationship_name: RELATIONSHIP_NAME & keyof RESOURCE[`relationships`],
		related_resource?: RELATED_RESOURCE
	) => {
	const relationship_is_many = json_api_schema[resource.type].relationships[relationship_name].many

	if (relationship_is_many) {
		assert(related_resource, `Must pass related_resource to remove from is_many relationship.`)

		const existing_relationship_data = resource.relationships[relationship_name]?.data || null

		const index_of_related_resource = Array.isArray(existing_relationship_data)
			&& existing_relationship_data.findIndex(({ id }) => id === related_resource.id)

		// @ts-expect-error typescript is confused about the relationships property
		resource.relationships[relationship_name] = {
			data: Array.isArray(existing_relationship_data) && index_of_related_resource
				? existing_relationship_data.splice(index_of_related_resource, 1)
				: existing_relationship_data,
		}
	} else {
		// @ts-expect-error typescript is confused about the relationships property
		resource.relationships[relationship_name] = { data: null }
	}

	return resource
}
